import { useMemo, type ReactNode } from "react";

import { type Conversation } from "shared/apiClient";
import { cn, sentenceCase, titleCase, type Noticeboard } from "shared/lib";

import { useShouldRenderSideBySide } from "@/hooks";

import { Separator } from "shared/components";
import { AnalysisSidebarContainer } from "./analysisSidebar";
import { useGetDevelopmentActivityText } from "./hooks";

export interface AnalysisLayoutProps {
  children?: (
    conversation: Conversation | undefined | null,
    noticeboardData: Noticeboard | undefined | null,
  ) => ReactNode;
  conversation: Conversation | undefined | null;
  messages: Conversation["messages"];
  noticeboardData: Noticeboard | undefined | null;
}

export function AnalysisLayout({
  children,
  conversation,
  messages,
  noticeboardData,
}: AnalysisLayoutProps) {
  const shouldShowSideBySide = useShouldRenderSideBySide();

  const developmentActivityText = useGetDevelopmentActivityText(
    conversation,
    noticeboardData,
  );

  const developmentActivity = useMemo(() => {
    return sentenceCase(developmentActivityText || "");
  }, [developmentActivityText]);

  return (
    <div className="flex h-full items-stretch justify-center">
      {shouldShowSideBySide ? (
        <>
          <div className="w-[280px] max-w-[280px] md:m-10">
            <AnalysisSidebarContainer
              conversation={conversation}
              messages={messages}
              noticeboardData={noticeboardData}
              subTitle={titleCase(conversation?.address || "")}
              title={developmentActivity}
            />
          </div>

          <Separator className="h-auto" orientation="vertical" />
        </>
      ) : null}

      <div
        className={cn(
          "md:m-10",
          shouldShowSideBySide && "w-[624px] max-w-[624px]",
        )}
      >
        {children?.(conversation, noticeboardData)}
      </div>
    </div>
  );
}
